import InfoContext from "pages/InfoContext";
import AlertPopup from "components/alert-popup/AlertPopup";
import DisplayMission from "./DisplayMission";
import Loader from "core/loader/LoaderV2";
import MissionsByFilters from "components/form/mission/MissionsByFilters";
import MissionsFilters from "components/form/mission/MissionsFilters";
import {
  POPUP_STATUS,
  QUERY_STATUS,
  DEFAULT_STATUS_MISSION,
  FILTER_KEYS,
  MISSION_STATUS_FILTERS,
} from "constants/constants";
import {
  formatFilterIntoString,
  getStringyfiedFilters,
  isValueEmpty,
  getDefaultMissionsFilters,
  getStoredMissionFilters,
  hasStoredMissionFilters,
  EMPTY_MISSION_FILTERS,
} from "utils/missionHelper";
import { useLocalStorage } from "react-use";
import { useBranchContext } from "api/settings/branch/BranchProviders";
import { useClientContext } from "api/directory/client/ClientProvider";
import { useEffect, useState, useMemo, useRef } from "react";
import { useMissionContext } from "api/mission/MissionProvider";
import { usePersonContext } from "api/directory/person/PersonProvider";
import { useUserContext } from "api/user/UserProvider";
import { SUCCESS_EDIT } from "constants/popup-codes";
import { canUserPerformAction } from "access-roles/general-functions";
import _ from "lodash";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { MISSION_ROLES } from "access-roles/roles";
import { useGeneralContext } from "api/directory/general/GeneralProvider";

const Mission = () => {
  const {
    getPersonsByProfile,
    getPersonsByIds,
    personsList,
    personsByProfileList,
  } = usePersonContext();
  const { branchList, branchListGetter } = useBranchContext();
  const { getClientsByName, clientsByName } = useClientContext();
  const {
    getMissionsByFilters,
    setMissionsByFilters,
    missionsByFilters,
    loadMissionStatus,
    setMissionById,
    setMissionBySlug,
    editMissionStatus,
    displayMission,
    setDisplayMission,
    getMissionBySlug,
    refresh,
    setRefresh,
    deleteMissionStatus,
  } = useMissionContext();
  const { lang, userScope, setIsTouched, userFilterStorage, getUserFilters } =
    useUserContext();
  const { getClientsByIds, clientsByIds } = useClientContext();
  const { userRoleList } = useUserContext();
  const [readOnly, setReadOnly] = useState(true);
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const [workersIds, setWorkersIds] = useState([]);
  const [clientsIds, setClientsIds] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { slug: urlSlug } = useParams();
  const [slug, setSlug] = useState(urlSlug || "");

  const { getNamesByFilters } = useGeneralContext();
  const [storedFilters, setStoredFilters] = useLocalStorage(
    "missions_filters",
    EMPTY_MISSION_FILTERS
  );
  const hadStoredAtMount = useRef(hasStoredMissionFilters(storedFilters));
  const [filters, setFilters] = useState(() =>
    hadStoredAtMount.current
      ? getStoredMissionFilters(storedFilters)
      : EMPTY_MISSION_FILTERS
  );
  const [isValidated, setIsValidated] = useState(false);
  const [missionFiltersLoaded, setMissionFiltersLoaded] = useState(false);
  const [isFromSpreadsheetPage, setIsFromSpreadsheetPage] = useState(false);
  const [defaultSearch, setDefaultSearch] = useState(!slug);
  const [isTemporaryFilter, setIsTemporaryFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { content } = missionsByFilters;
  const [namesForFilters, setNamesForFilters] = useState([]);
  const [namesForMissions, setNamesForMissions] = useState([]);
  const [workersType, setWorkersType] = useState([]);

  useEffect(() => {
    setSlug(urlSlug || "");
  }, [urlSlug]);

  useEffect(() => {
    setDisplayMission(!!slug);
  }, [slug]);

  useEffect(() => {
    !isFromSpreadsheetPage &&
      setIsFromSpreadsheetPage(location.state?.from === "spreadsheet");
  }, [location.state]);

  const fetchNamesForFilters = async (params) => {
    try {
      const result = await getNamesByFilters(params);
      setNamesForFilters(result);
      return result;
    } catch (error) {
      console.error("fetchNamesForFilters error:", error);
    }
  };

  const fetchNamesForMissions = async (params) => {
    try {
      const result = await getNamesByFilters(params);
      setNamesForMissions(result);
      return result;
    } catch (error) {
      console.error("fetchNamesForMissions error:", error);
    }
  };

  const fetchWorkersType = async (param) => {
    try {
      const result = await getNamesByFilters(param);
      const formattedResult = result?.map((e) => {
        return {
          worker_id: e?.id,
          worker_type: e?.profile_id,
        };
      });
      setWorkersType((old) => [...old, ...formattedResult]);
    } catch (error) {
      console.error("fetchWorkersType error:", error);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setMissionFiltersLoaded(false);
    (async () => {
      try {
        await getUserFilters(FILTER_KEYS.MISSION);
      } finally {
        if (!cancelled) setMissionFiltersLoaded(true);
      }
    })();
    branchListGetter();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (location.state?.dashboardFilter) {
      const { status } = location.state.dashboardFilter;

      // Handle both single status and array of statuses
      const statusArray = Array.isArray(status) ? status : [status];
      const formattedStatuses = statusArray
        .map((statusValue) => {
          // Find the matching status configuration from MISSION_STATUS_FILTERS
          const statusConfig = MISSION_STATUS_FILTERS.find(
            (s) => s.value === statusValue
          );
          if (!statusConfig) {
            console.warn(`Status configuration not found for: ${statusValue}`);
            return null;
          }
          return statusConfig;
        })
        .filter(Boolean);

      setFilters({
        status: formattedStatuses,
        startFromDate: "",
        startToDate: "",
        endFromDate: "",
        endToDate: "",
      });
      setIsTemporaryFilter(true); // Mark as temporary to prevent saving
      setIsValidated(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    if (hadStoredAtMount.current) return;
    if (!location.state?.dashboardFilter && !_.isEmpty(userFilterStorage)) {
      setFilters((prev) => ({
        ...prev,
        ...getDefaultMissionsFilters({ userFilterStorage }),
      }));
    }
  }, [userFilterStorage]);

  useEffect(() => {
    if (isTemporaryFilter) return;
    setStoredFilters({
      status: filters.status || [],
      workers: filters.workers || [],
      clients: filters.clients || [],
      missionTitle: filters.missionTitle || "",
      managers: filters.managers || [],
      startFromDate: filters.startFromDate || "",
      startToDate: filters.startToDate || "",
      endFromDate: filters.endFromDate || "",
      endToDate: filters.endToDate || "",
      signedBy: filters.signedBy || [],
      chargedBy: filters.chargedBy || [],
    });
  }, [
    filters.status,
    filters.workers,
    filters.clients,
    filters.missionTitle,
    filters.managers,
    filters.startFromDate,
    filters.startToDate,
    filters.endFromDate,
    filters.endToDate,
    filters.signedBy,
    filters.chargedBy,
    isTemporaryFilter,
  ]);

  useEffect(() => {
    if (!missionFiltersLoaded) return;
    if (isValidated || defaultSearch) {
      if (
        filters &&
        Object.keys(filters).length > 0 &&
        !Object.values(filters).every(isValueEmpty)
      ) {
        search();
        setDefaultSearch(false);
      }
    }
  }, [isValidated, userScope, filters, missionFiltersLoaded]);

  useEffect(() => {
    setDefaultSearch(true);
  }, [userScope]);

  const search = (page = currentPage) => {
    if (!filters || _.isEmpty(filters)) return;
    const {
      status,
      workers,
      clients,
      missionTitle,
      managers,
      startFromDate,
      endFromDate,
      startToDate,
      endToDate,
      signedBy,
      chargedBy,
    } = filters;

    const formattedFilters = {
      status: formatFilterIntoString(status) || "",
      workers_ids: formatFilterIntoString(workers) || "",
      clients_ids: formatFilterIntoString(clients) || "",
      search: missionTitle || "",
      managers_ids: formatFilterIntoString(managers),
      start_from_date: startFromDate || "",
      end_from_date_or_null: endFromDate || "",
      start_to_date: startToDate || "",
      end_to_date: endToDate || "",
      signed_by: formatFilterIntoString(signedBy) || "",
      charged_by: formatFilterIntoString(chargedBy) || "",
    };

    const cleanFilters = _.pickBy(formattedFilters, Boolean);
    const stringyfiedFilters = getStringyfiedFilters(cleanFilters).replace(
      /"/g,
      ""
    );
    const stringyfiedFiltersToSave = JSON.stringify(cleanFilters);

    if (_.isEmpty(stringyfiedFilters))
      setFilters({
        status: DEFAULT_STATUS_MISSION,
        startFromDate: "",
        startToDate: "",
        endFromDate: "",
        endToDate: "",
      });

    if (typeof stringyfiedFilters === "string") {
      setIsValidated(false);
      // Only save filters if not a temporary dashboard filter
      const shouldSaveFilters = !isTemporaryFilter;
      setIsTemporaryFilter(false); // Reset after first search

      return getMissionsByFilters({
        page: page,
        filters: JSON.stringify(stringyfiedFilters).replace(/"/g, ""),
        filtersToSave: shouldSaveFilters ? stringyfiedFiltersToSave : null,
        userScope: userScope,
      });
    }
  };

  useEffect(() => {
    if (slug && deleteMissionStatus === QUERY_STATUS.INITIAL) {
      window.history.pushState({}, "", `/${userScope}/missions/${slug}`);
      getMissionBySlug({ slug });
      setRefresh(false);
    }
  }, [slug, refresh, userScope]);

  useEffect(() => {
    if (loadMissionStatus === QUERY_STATUS.NOT_FOUND) {
      navigate(`/${userScope}/not-found`, { replace: true });
    }
  }, [loadMissionStatus, userScope, navigate]);

  useEffect(() => {
    setRefresh(false);
  }, [location.pathname, refresh]);

  useEffect(() => {
    const pageNewClients = _.chain(missionsByFilters?.content)
      .map("client_id")
      .uniq()
      .filter((elm) => !_.includes(clientsIds, elm))
      .value();
    setClientsIds((old) => [...old, ...pageNewClients]);

    if (pageNewClients?.length > 0) {
      getClientsByIds({ ids: _.join(pageNewClients, ",") });
    }

    const pageNewWorkers = _.chain(missionsByFilters?.content)
      .map((elm) => [elm?.manager_id, elm?.worker_id])
      .flattenDeep()
      .uniq()
      .filter((elm) => !_.includes(workersIds, elm))
      .value();
    setWorkersIds((old) => [...old, ...pageNewWorkers]);

    if (pageNewWorkers?.length > 0) {
      const ids = _.join(pageNewWorkers, ",");
      fetchWorkersType({ id: pageNewWorkers, types: ["person"] });
      getPersonsByIds({ ids });
    }
  }, [missionsByFilters, userScope]);

  const getMoreMissions = () => {
    setCurrentPage((prevPage) => {
      const nextPage = prevPage + 1;
      search(nextPage);
      return nextPage;
    });
  };

  const initDisplayMission = () => {
    setSlug("");
    setMissionById({});
    setMissionBySlug({});
    setReadOnly(true);
    setDisplayMission(false);
    setIsValidated(false);
    window.history.pushState({}, "", `/${userScope}/missions`);
  };

  const linkToMissionBtnHandler = ({ isEditBtn = false, missionSlug }) => {
    setSlug(missionSlug);
    setDisplayMission(true);
    isEditBtn && setReadOnly(false);
    getMissionBySlug({ slug: missionSlug });
  };

  useEffect(() => {
    if (editMissionStatus === QUERY_STATUS.SUCCESS) {
      setStatus(POPUP_STATUS.SUCCESS);
      setCode(SUCCESS_EDIT);
      setReadOnly(true);
    }
  }, [editMissionStatus]);


  const value = useMemo(
    () => ({
      filters,
      getOptions: getPersonsByProfile,
      branchList,
      getClients: getClientsByName,
      setIsTouched,
      readOnly,
      displayMission,
      setDisplayMission,
      getMission: getMissionBySlug,
      linkToMissionBtnHandler,
      initDisplayMission,
      lang: lang,
      personsList,
      getMoreMissions,
      loadMissionStatus,
      setReadOnly,
      canEdit: canUserPerformAction({
        userRoles: userRoleList,
        requiredRoles: MISSION_ROLES.ROLE_UPDATE_MISSION,
      }),
      clientsList: clientsByName,
      clientsByIds,
      setSlug,
      slug,
      personsByProfileList,
      missionsList: missionsByFilters,
      setMissionsByFilters,
      setCurrentPage,
      currentPage,
      fetchNamesForFilters,
      namesForFilters,
      fetchNamesForMissions,
      namesForMissions,
      workersType,
      isMissionsPage: true,
      location,
      navigate,
      isFromSpreadsheetPage,
      userScope,
    }),
    [
      setCurrentPage,
      fetchNamesForFilters,
      namesForFilters,
      fetchNamesForMissions,
      namesForMissions,
      currentPage,
      filters,
      missionsByFilters,
      personsByProfileList,
      slug,
      setSlug,
      userRoleList,
      loadMissionStatus,
      clientsByIds,
      getPersonsByProfile,
      personsList,
      branchList,
      lang,
      getClientsByName,
      clientsByName,
      setIsTouched,
      readOnly,
      displayMission,
      setDisplayMission,
      userScope,
      workersType,
      linkToMissionBtnHandler,
      initDisplayMission,
      location,
      navigate,
      isFromSpreadsheetPage,
      userScope,
    ]
  );

  const renderMission = () => {
    return displayMission ? (
      <DisplayMission setCode={setCode} setStatus={setStatus} code={code} />
    ) : null;
  };

  const renderContent = () => {
    if (displayMission) {
      return renderMission();
    }
    if (loadMissionStatus === QUERY_STATUS.LOADING && !content?.length) {
      return <Loader />;
    }
    if (content?.length > 0) {
      return (
        <MissionsByFilters currentPage={currentPage} userRole={userRoleList} />
      );
    }
    return null;
  };

  return (
    <InfoContext.Provider value={value}>
      <div>
        <AlertPopup
          code={code}
          setCode={setCode}
          setStatus={setStatus}
          status={status}
        />
        <MissionsFilters
          filters={filters}
          setFilters={setFilters}
          search={search}
        />
        {renderContent()}
      </div>
    </InfoContext.Provider>
  );
};

export default Mission;
